import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue";
const _hoisted_1 = { class: "logout-page" };
const _hoisted_2 = {
    class: "w-full max-w-[525px] flex flex-col px-6",
    "aria-labelledby": "logout-title"
};
const _hoisted_3 = { class: "flex flex-col gap-5 text-left mb-12" };
const _hoisted_4 = {
    id: "logout-title",
    class: "title-text"
};
const _hoisted_5 = { class: "subtitle-text" };
const _hoisted_6 = ["aria-label"];
const _hoisted_7 = ["aria-label"];
const _hoisted_8 = ["disabled", "aria-label", "aria-busy"];
const _hoisted_9 = {
    key: 0,
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    "stroke-width": "2",
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    "aria-hidden": "true",
    focusable: "false"
};
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuth } from "@/shared/composables/useAuth";
import { useLogger } from "@/shared/composables/useLogger";
import { isLanguageSupported, normalizeLocale } from "@/shared/utils/localeUtils";
import { useLocale } from "@/shared/composables/useLocale.ts";
import { getLoginEmail, clearLoginEmail } from "@/shared/utils/authCleanup";
export default /*@__PURE__*/ _defineComponent({
    __name: 'Logout',
    setup(__props) {
        const logger = useLogger();
        const router = useRouter();
        const { isAuthenticated, login } = useAuth();
        const { setLocale } = useLocale();
        const isLoggingIn = ref(false);
        const route = useRoute();
        const isNoOrgReason = computed(() => route.query.reason === "no-org");
        const titleKey = computed(() => isNoOrgReason.value ? "logout.noOrganizationAccess.title" : "logout.title");
        const subtitleKey = computed(() => isNoOrgReason.value ? "logout.noOrganizationAccess.body" : "logout.subtitle");
        // Check if user is already authenticated and redirect
        onMounted(() => {
            // On logout page, ensure we use the user's manually selected locale (from localStorage)
            // This prevents Keycloak from overriding the user's choice
            const selectedLocale = localStorage.getItem("selectedLocale") || localStorage.getItem("locale");
            if (selectedLocale && isLanguageSupported(selectedLocale)) {
                const translationLocale = normalizeLocale(selectedLocale);
                setLocale(translationLocale, "manual");
                logger.debug(`Logout page: Setting locale to user's choice: ${selectedLocale} (translation: ${translationLocale})`);
            }
            if (isAuthenticated.value) {
                logger.debug("User is already authenticated, redirecting to dashboard");
                router.push("/dashboard");
            }
            // Watch for authentication changes
            const stopWatching = watch(isAuthenticated, (newValue) => {
                if (newValue) {
                    logger.debug("User became authenticated, redirecting to dashboard");
                    router.push("/dashboard");
                }
            });
            // Cleanup watcher on unmount
            onUnmounted(() => {
                stopWatching();
            });
        });
        const handleLogin = async () => {
            if (isLoggingIn.value)
                return;
            isLoggingIn.value = true;
            try {
                // Get saved email for login_hint (Keycloak official parameter)
                const savedEmail = getLoginEmail();
                if (savedEmail) {
                    logger.debug(`Reconnecting with login_hint: ${savedEmail} - Keycloak will pre-fill/skip email entry`);
                }
                else {
                    logger.debug("No saved email - will show full login flow from email entry");
                }
                // Clear force-logout flag to allow reconnection
                sessionStorage.removeItem("force-logout");
                // Clear any existing redirect path and set dashboard as target
                sessionStorage.removeItem("auth-redirect-path");
                sessionStorage.setItem("auth-redirect-path", "/dashboard");
                // Trigger login with login_hint if available
                // This is the official Keycloak way to pre-fill email
                if (savedEmail) {
                    await login(window.location.origin + "/dashboard", savedEmail);
                }
                else {
                    await login(window.location.origin + "/dashboard");
                }
                // Login will handle the redirect, but as a fallback
                setTimeout(() => {
                    if (isAuthenticated.value) {
                        router.push("/dashboard");
                    }
                }, 1000);
            }
            catch (error) {
                logger.debug("Login failed:", error);
                isLoggingIn.value = false;
            }
        };
        const handleChangeOrganisation = async () => {
            if (isLoggingIn.value)
                return;
            isLoggingIn.value = true;
            try {
                logger.debug("Changing organization - clearing saved data to force full login flow");
                // Clear saved email to force email entry screen
                clearLoginEmail();
                // Clear saved organization to force organization selection
                localStorage.removeItem("organizationCache");
                logger.debug("Cleared organization cache to allow new selection");
                // Clear force-logout flag
                sessionStorage.removeItem("force-logout");
                // Clear any existing redirect path and set dashboard as target
                sessionStorage.removeItem("auth-redirect-path");
                sessionStorage.setItem("auth-redirect-path", "/dashboard");
                // Trigger login without login_hint - will start from email entry
                await login(window.location.origin + "/dashboard");
                setTimeout(() => {
                    if (isAuthenticated.value) {
                        router.push("/dashboard");
                    }
                }, 1000);
            }
            catch (error) {
                logger.debug("Organization change failed:", error);
                isLoggingIn.value = false;
            }
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _cache[1] || (_cache[1] = _createElementVNode("div", {
                        class: "logo-text mb-8",
                        "aria-hidden": "true"
                    }, " EUDONET ", -1 /* CACHED */)),
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("h1", _hoisted_4, _toDisplayString(_ctx.$t(titleKey.value)), 1 /* TEXT */),
                        _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t(subtitleKey.value)), 1 /* TEXT */)
                    ]),
                    _createElementVNode("nav", {
                        class: "buttons-container",
                        "aria-label": _ctx.$t('logout.actionsLabel')
                    }, [
                        _createElementVNode("button", {
                            type: "button",
                            class: "btn-secondary",
                            "aria-label": _ctx.$t('logout.changeOrganisation'),
                            onClick: handleChangeOrganisation
                        }, [
                            _createElementVNode("span", null, _toDisplayString(_ctx.$t("logout.changeOrganisation")), 1 /* TEXT */)
                        ], 8 /* PROPS */, _hoisted_7),
                        _createElementVNode("button", {
                            type: "button",
                            disabled: isLoggingIn.value,
                            class: "btn-primary",
                            "aria-label": isLoggingIn.value ? _ctx.$t('login.loggingIn') : _ctx.$t('logout.reconnect'),
                            "aria-busy": isLoggingIn.value,
                            onClick: handleLogin
                        }, [
                            _createElementVNode("span", null, _toDisplayString(isLoggingIn.value ? _ctx.$t("login.loggingIn") : _ctx.$t("logout.reconnect")), 1 /* TEXT */),
                            (!isLoggingIn.value)
                                ? (_openBlock(), _createElementBlock("svg", _hoisted_9, [...(_cache[0] || (_cache[0] = [
                                        _createElementVNode("path", { d: "M5 12h14" }, null, -1 /* CACHED */),
                                        _createElementVNode("path", { d: "m12 5 7 7-7 7" }, null, -1 /* CACHED */)
                                    ]))]))
                                : _createCommentVNode("v-if", true)
                        ], 8 /* PROPS */, _hoisted_8)
                    ], 8 /* PROPS */, _hoisted_6)
                ])
            ]));
        };
    }
});
